import React, { useState, useCallback } from 'react';
import { Image as ImageIcon, FileText, FileSpreadsheet, Paperclip } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { ProductTemplate, Attachment } from '../types';

export interface UseRecipeAttachmentsProps {
  userId: string;
  editingRecipe: Partial<ProductTemplate> | null;
  setEditingRecipe: React.Dispatch<React.SetStateAction<Partial<ProductTemplate> | null>>;
}

export interface UseRecipeAttachmentsReturn {
  isUploading: boolean;
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  removeAttachment: (id: string) => void;
  getFileIcon: (type: string) => React.ReactNode;
}

export function useRecipeAttachments({
  userId,
  editingRecipe,
  setEditingRecipe
}: UseRecipeAttachmentsProps): UseRecipeAttachmentsReturn {
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingRecipe) return;

    if (file.size > 10 * 1024 * 1024) {
      alert('O arquivo deve ter no máximo 10MB');
      return;
    }

    setIsUploading(true);
    const fileId = crypto.randomUUID();
    const fileExt = file.name.split('.').pop() || '';
    const fileName = `${userId}/${fileId}.${fileExt}`;

    try {
      const { error: uploadError } = await supabase.storage
        .from('recipe-attachments')
        .upload(fileName, file, { upsert: true });

      if (uploadError) {
        throw uploadError;
      }

      const { data: publicUrlData } = supabase.storage
        .from('recipe-attachments')
        .getPublicUrl(fileName);

      if (!publicUrlData || !publicUrlData.publicUrl) {
        throw new Error('Não foi possível obter a URL pública do anexo.');
      }

      const newAttachment: Attachment = {
        id: fileId,
        name: file.name,
        url: publicUrlData.publicUrl,
        type: file.type,
        size: file.size
      };

      setEditingRecipe(prev => {
        if (!prev) return null;
        return {
          ...prev,
          attachments: [...(prev.attachments || []), newAttachment]
        };
      });
    } catch (err: unknown) {
      console.error('Erro no upload:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      alert(`Falha ao enviar arquivo: ${errorMessage}. Verifique se o bucket "recipe-attachments" existe no seu Supabase.`);
    } finally {
      setIsUploading(false);
      // Reset raw DOM input value to prevent memory leaks or retention
      e.target.value = '';
    }
  }, [userId, editingRecipe, setEditingRecipe]);

  const removeAttachment = useCallback((id: string) => {
    setEditingRecipe(prev => {
      if (!prev) return null;
      return {
        ...prev,
        attachments: (prev.attachments || []).filter(a => a.id !== id)
      };
    });
  }, [setEditingRecipe]);

  const getFileIcon = useCallback((type: string): React.ReactNode => {
    if (type.includes('image')) {
      return React.createElement(ImageIcon, { className: 'h-4 w-4 text-blue-500' });
    }
    if (type.includes('pdf')) {
      return React.createElement(FileText, { className: 'h-4 w-4 text-rose-500' });
    }
    if (type.includes('spreadsheet') || type.includes('excel') || type.includes('csv')) {
      return React.createElement(FileSpreadsheet, { className: 'h-4 w-4 text-emerald-500' });
    }
    return React.createElement(Paperclip, { className: 'h-4 w-4 text-zinc-400' });
  }, []);

  return {
    isUploading,
    handleFileUpload,
    removeAttachment,
    getFileIcon
  };
}
